import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { House, Broadcast, MusicNotes, Newspaper, Users, FolderSimple, SignOut, Database, UserCircleGear, ShieldCheck, Lock, ArrowsClockwise } from "@phosphor-icons/react";
import { seedData, syncUploads, importSupabase } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const adminNav = [
  { to: "/admin", label: "Dashboard", end: true, icon: House },
  { to: "/admin/streams", label: "Live Streams", icon: Broadcast },
  { to: "/admin/content", label: "Content", icon: MusicNotes },
  { to: "/admin/articles", label: "Articles", icon: Newspaper },
  { to: "/admin/reciters", label: "Reciters", icon: Users },
  { to: "/admin/categories", label: "Categories", icon: FolderSimple },
  { to: "/admin/users", label: "Users & Roles", icon: UserCircleGear },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();

  const onLogout = () => {
    logout();
    nav("/");
  };

  const onSeed = async () => {
    try {
      const r = await seedData();
      toast.success(`Seeded: ${r.tracks} tracks, ${r.categories} categories`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const onSync = async () => {
    try {
      toast.info("Uploads sync ho rahi hai...");
      const r = await syncUploads();
      toast.success(`Sync mukammal: ${r.added} naye tracks, ${r.skipped} already the`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync fail ho gaya");
    }
  };

  const onImport = async () => {
    try {
      toast.info("Supabase se data import ho raha hai...");
      const r = await importSupabase();
      toast.success(`Import mukammal! ${r.added} tracks, ${r.reciters_created} reciters bane`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fail ho gaya");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#A1A1AA] text-sm">Loading...</div>
      </div>
    );
  }

  // Na logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md border border-[#2A2A2D] bg-[#121214] p-10 text-center">
          <Lock size={36} weight="duotone" className="text-[#B79C7B] mx-auto mb-4" />
          <div className="overline text-[#B79C7B] mb-3">Admin Panel</div>
          <h1 className="font-serif text-2xl text-ivory">Pehle login karein</h1>
          <p className="text-sm text-[#A1A1AA] mt-2">Admin panel sirf admin account se accessible hai</p>
          <Link
            to="/login"
            className="mt-6 w-full h-12 bg-[#8A2E2E] hover:bg-[#A33B3B] transition-colors text-sm tracking-widest flex items-center justify-center"
          >
            LOGIN
          </Link>
        </div>
      </div>
    );
  }

  // Logged in lekin admin nahi
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md border border-[#8A2E2E] bg-[#8A2E2E]/10 p-10 text-center">
          <ShieldCheck size={36} weight="duotone" className="text-[#FF3B30] mx-auto mb-4" />
          <div className="overline text-[#FF3B30] mb-3">Access Denied</div>
          <h1 className="font-serif text-2xl text-ivory">Admin sirf admin ja sakta hai</h1>
          <p className="text-sm text-[#A1A1AA] mt-2">
            Aap ka role <span className="font-mono text-ivory">{user.role}</span> hai — admin access nahi hai
          </p>
          <Link to="/" className="mt-6 inline-block text-sm text-[#B79C7B] hover:underline">
            Wapas Home
          </Link>
        </div>
      </div>
    );
  }

  // Admin — panel dikhao
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="border-r border-[#2A2A2D] bg-[#0A0A0B] lg:min-h-screen">
        <div className="p-6 border-b border-[#2A2A2D]">
          <div className="overline text-[#B79C7B] flex items-center gap-2">
            <ShieldCheck size={14} weight="fill" /> Control Room
          </div>
          <div className="font-serif text-2xl text-ivory mt-1">Admin Panel</div>
          <div className="text-xs text-[#71717A] mt-1 font-mono">{user.email}</div>
        </div>
        <nav className="p-4 space-y-1">
          {adminNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? "bg-[#1A1A1D] text-ivory border-l-2 border-[#8A2E2E]" : "text-[#A1A1AA] hover:bg-[#121214] hover:text-ivory border-l-2 border-transparent"}`
                }
              >
                <Icon size={18} weight="duotone" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 mt-4 space-y-2 border-t border-[#2A2A2D]">
          <button onClick={onImport} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#B79C7B] hover:text-ivory border border-[#B79C7B] hover:bg-[#B79C7B]/10 mb-2 transition-colors">
            <ArrowsClockwise size={18} weight="duotone" /> Import All Nohay
          </button>
          <button onClick={onSync} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#B79C7B] hover:text-ivory border border-[#2A2A2D] hover:border-[#B79C7B] mb-2 transition-colors" data-testid="admin-sync-button">
            <ArrowsClockwise size={18} weight="duotone" /> Sync Uploads (Nohay)
          </button>
          <button onClick={onSeed} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#A1A1AA] hover:text-ivory" data-testid="admin-seed-button">
            <Database size={18} weight="duotone" /> Seed Sample Data
          </button>
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#A1A1AA] hover:text-[#FF3B30]" data-testid="admin-logout-button">
            <SignOut size={18} weight="duotone" /> Logout
          </button>
        </div>
      </aside>
      <main className="p-6 lg:p-10">
        <Outlet />
      </main>
    </div>
  );
}
